<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BoxberryPickUpPoint from '~/components/cart/shipping/BoxberryPickUpPoint.vue'
import CdekPickupPoint from '~/components/cart/shipping/CdekPickupPoint.vue'
import DalliCourier from '~/components/cart/shipping/DalliCourier.vue'
import FivePostPickUpPoint from '~/components/cart/shipping/FivePostPickUpPoint.vue'
import LocalPickUp from '~/components/cart/shipping/LocalPickUp.vue'
import RussianPostCourier from '~/components/cart/shipping/RussianPostCourier.vue'
import RussianPostInternationalParcel from '~/components/cart/shipping/RussianPostInternationalParcel.vue'
import RussianPostPickUpPoint from '~/components/cart/shipping/RussianPostPickUpPoint.vue'
import YandexDeliveryPickUpPoint from '~/components/cart/shipping/YandexDeliveryPickUpPoint.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import YesNoSwitch from '~/components/form-elements/YesNoSwitch.vue'
import UrgentOrder from '~/components/modal-windows/UrgentOrder.vue'
import RussianPostPickUpPointOrCourier from '~/components/modal-windows/shipping/RussianPostPickUpPointOrCourier.vue'
import BankCardType from '~/enums/bank-card-type'
import Destination from '~/enums/destination'
import OrderShippingType from '~/enums/order-shipping-type'
import PickUpPoint from '~/enums/pick-up-point'
import helpers from '~/helpers'
import { useCartStore } from '~/store/cart'
import type { OrderShippingType as CartOrderShippingType } from '~/types/cart'
import type { RadioButtonChoice } from '~/types/form-elements'
import type { LocalPickUpShippingData } from '~/types/shipping'

const destinationChoices = Destination.sortedMembers.map(
  (x: Destination): RadioButtonChoice => ({ labelText: Destination.getName(x), value: x })
)

const cartStore = useCartStore()

const destination = ref(Destination.InRussia)

const orderShippingTypeChoices = computed(() => {
  const orderShippingTypeIds = cartStore.orderShippingTypes.map((x: CartOrderShippingType): string => x.id)

  return OrderShippingType
    .sortedMembers
    .filter(
      (x: OrderShippingType): boolean => {
        if (!orderShippingTypeIds.includes(x)) {
          return false
        }

        const bankCardTypeForOrderShippingType = OrderShippingType.getBankCardType(x)

        if (bankCardTypeForOrderShippingType === BankCardType.Russian && destination.value !== Destination.InRussia) {
          return false
        }

        if (
          bankCardTypeForOrderShippingType === BankCardType.International
          && destination.value !== Destination.ToOtherCountries
        ) {
          return false
        }

        return true
      }
    )
    .map(
      (x: OrderShippingType): RadioButtonChoice => ({
        iconDescription: OrderShippingType.getIconDescription(x),
        iconLabel: OrderShippingType.isDeliveryToPickUpPoint(x)
        && cartStore.orderShippingTypes.find((y: CartOrderShippingType): boolean => y.id === x)
          ?.minOrderSubtotalAmountInKopecksForFreeShipping === 0
          ? { backgroundColor: '#59ad3b', color: '#ffffff', title: 'Бесплатно' }
          : null,
        iconName: OrderShippingType.getIconName(x),
        labelText: OrderShippingType.getName(x),
        value: x
      })
    )
})
const showDestinationRadioButtons = computed(
  () => !orderShippingTypeChoices.value.every(
    (x: RadioButtonChoice): boolean => OrderShippingType.getBankCardType(x.value) === BankCardType.Any
  )
)
const showUrgentOrderSwitch = computed(
  () => cartStore.urgentOrderPriceInKopecks !== null
    && cartStore.orderShippingType !== null
    && !(
      cartStore.orderShippingType === OrderShippingType.LocalPickUp
      && (cartStore.shippingData as LocalPickUpShippingData)?.pickUpPointId !== PickUpPoint.Golubinskaya
    )
)

watch(orderShippingTypeChoices, (): void => {
  if (cartStore.orderShippingType === null) {
    return
  }

  if (OrderShippingType.hasShippingData(cartStore.orderShippingType)) {
    cartStore.shippingCostInKopecks = null
    cartStore.shippingData = null
  }

  cartStore.orderShippingType = null
})
watch(showUrgentOrderSwitch, (): void => {
  cartStore.isUrgentOrder = false
})

async function openRussianPostPickUpPointOrCourierModalWindow (): Promise<void> {
  await useModal({
    component: RussianPostPickUpPointOrCourier,
    attrs: { declaredValueInKopecks: cartStore.subtotalWithDiscountInKopecks }
  }).open()
}

async function openUrgentOrderModalWindow (): Promise<void> {
  await useModal({ component: UrgentOrder }).open()
}
</script>

<template>
  <BlockWithBorder>
    <div class="h4">
      Выберите способ получения
    </div>

    <template v-if="orderShippingTypeChoices.length">
      <RadioButtons
        v-if="showDestinationRadioButtons"
        v-model="destination"
        :choices="destinationChoices"
        class="mb-6"
        label-text="Куда"
        name="destination"
      />

      <RadioButtons
        v-model="cartStore.orderShippingType"
        :choices="orderShippingTypeChoices"
        is-vertical
        label-text="Способ получения"
        name="orderShippingType"
        with-blocks
      />

      <LocalPickUp v-if="cartStore.orderShippingType === OrderShippingType.LocalPickUp" />
      <template v-else-if="cartStore.orderShippingType === OrderShippingType.BoxberryPickUpPoint">
        <Notification v-if="destination === Destination.ToOtherCountries" class="my-6" is-small status="info">
          Boxberry доставляет в&nbsp;Казахстан, Беларусь, Киргизию и&nbsp;Армению.
        </Notification>

        <BoxberryPickUpPoint />
      </template>
      <CdekPickupPoint v-else-if="cartStore.orderShippingType === OrderShippingType.CdekPickupPoint" />
      <FivePostPickUpPoint v-else-if="cartStore.orderShippingType === OrderShippingType.FivePostPickUpPoint" />
      <RussianPostPickUpPoint
        v-else-if="cartStore.orderShippingType === OrderShippingType.RussianPostPickUpPoint"
        @open-russian-post-pick-up-point-or-courier-modal-window="openRussianPostPickUpPointOrCourierModalWindow"
      />
      <RussianPostCourier
        v-else-if="cartStore.orderShippingType === OrderShippingType.RussianPostCourier"
        @open-russian-post-pick-up-point-or-courier-modal-window="openRussianPostPickUpPointOrCourierModalWindow"
      />
      <RussianPostInternationalParcel
        v-else-if="cartStore.orderShippingType === OrderShippingType.RussianPostInternationalParcel"
        :promo-code-name="cartStore.form.promoCodeName"
      />
      <YandexDeliveryPickUpPoint
        v-else-if="cartStore.orderShippingType === OrderShippingType.YandexDeliveryPickUpPoint"
      />
      <DalliCourier v-else-if="cartStore.orderShippingType === OrderShippingType.DalliCourier" />
      <Notification
        v-else-if="cartStore.orderShippingType === OrderShippingType.BoxberryCourier"
        class="mt-6"
        is-small
        status="info"
      >
        Стоимость доставки будет рассчитана на&nbsp;следующем шаге.
      </Notification>
    </template>
    <Notification v-else is-small status="warning">
      Для выбранных товаров нет общего способа получения. Разбейте заказ на&nbsp;части, временно удалив некоторые товары из&nbsp;корзины.
    </Notification>

    <template v-if="showUrgentOrderSwitch">
      <YesNoSwitch v-model="cartStore.isUrgentOrder" class="mt-6" label-text="Срочный заказ" />
      <div class="mt-2 text-sm">
        Цена: {{ helpers.formatPrice(cartStore.urgentOrderPriceInKopecks, true) }}&nbsp;₽. <a href="#" @click.prevent="openUrgentOrderModalWindow">Подробнее об&nbsp;услуге</a>.
      </div>
    </template>
  </BlockWithBorder>
</template>
